import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
} from '@mui/material';
import {
  Close,
  Support,
  Security,
  Gavel,
  Policy,
  Email,
  Phone,
  LocationOn,
  AccessTime,
} from '@mui/icons-material';

const LegalModal = ({ open, onClose, contentType, theme }) => {
  const getContent = () => {
    switch (contentType) {
      case 'support':
        return {
          title: 'Support & Help Center',
          icon: <Support />,
          content: (
            <>
              <Typography variant="h6" gutterBottom sx={{ color: theme.accent || theme.primary }}>
                24/7 Customer Support
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                Our dedicated support team is available around the clock to assist you with any
                questions or issues.
              </Typography>

              <Box sx={{ my: 3 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                  sx={{ color: theme.light || '#fff' }}
                >
                  Contact Methods:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Phone sx={{ color: theme.accent || theme.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Phone Support"
                      secondary="+27 (0) 123 456 789 - Available 24/7"
                      primaryTypographyProps={{ sx: { color: theme.light || '#fff' } }}
                      secondaryTypographyProps={{
                        sx: { color: alpha(theme.light || '#fff', 0.7) },
                      }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Email sx={{ color: theme.accent || theme.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email Support"
                      secondary="support@fleetcam.com - Response within 2 hours"
                      primaryTypographyProps={{ sx: { color: theme.light || '#fff' } }}
                      secondaryTypographyProps={{
                        sx: { color: alpha(theme.light || '#fff', 0.7) },
                      }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <AccessTime sx={{ color: theme.accent || theme.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Business Hours"
                      secondary="Mon-Fri: 8:00 AM - 6:00 PM SAST"
                      primaryTypographyProps={{ sx: { color: theme.light || '#fff' } }}
                      secondaryTypographyProps={{
                        sx: { color: alpha(theme.light || '#fff', 0.7) },
                      }}
                    />
                  </ListItem>
                </List>
              </Box>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: theme.light || '#fff' }}
              >
                Common Topics:
              </Typography>
              <Typography
                component="div"
                variant="body2"
                sx={{ color: alpha(theme.light || '#fff', 0.8) }}
              >
                • Account access and password reset
                <br />
                • Technical issues and troubleshooting
                <br />
                • Fleet management assistance
                <br />
                • Billing and subscription queries
                <br />• Feature requests and feedback
              </Typography>
            </>
          ),
        };

      case 'privacy':
        return {
          title: 'Privacy Policy',
          icon: <Security />,
          content: (
            <>
              <Typography
                variant="body2"
                sx={{ color: alpha(theme.light || '#fff', 0.6) }}
                paragraph
              >
                Last updated: January 1, 2025
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ color: theme.accent || theme.primary }}>
                Your Privacy Matters
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                FleetCam Pty Ltd ("we", "our", or "us") is committed to protecting your privacy.
                This Privacy Policy explains how we collect, use, and safeguard your information
                when you use our fleet management services.
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ mt: 3, color: theme.light || '#fff' }}
              >
                Information We Collect
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                • <strong>Account Information:</strong> Name, email, company details, and login
                credentials
                <br />• <strong>Fleet Data:</strong> Vehicle information, GPS locations, driver
                behavior metrics
                <br />• <strong>Usage Data:</strong> How you interact with our platform and services
                <br />• <strong>Device Information:</strong> IP addresses, browser types, and device
                identifiers
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: theme.light || '#fff' }}
              >
                How We Use Your Information
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                • Provide and maintain our fleet management services
                <br />
                • Improve and personalize your experience
                <br />
                • Send important updates and notifications
                <br />
                • Ensure platform security and prevent fraud
                <br />• Comply with legal obligations
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: theme.light || '#fff' }}
              >
                Data Security
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                We implement industry-standard security measures including encryption, secure
                servers, and regular security audits to protect your data. Access is restricted to
                authorized personnel only.
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: theme.light || '#fff' }}
              >
                Your Rights
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                You have the right to access, update, or delete your personal information. Contact
                us at privacy@fleetcam.com for any privacy-related requests.
              </Typography>
            </>
          ),
        };

      case 'terms':
        return {
          title: 'Terms of Service',
          icon: <Gavel />,
          content: (
            <>
              <Typography
                variant="body2"
                sx={{ color: alpha(theme.light || '#fff', 0.6) }}
                paragraph
              >
                Effective Date: January 1, 2025
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ color: theme.accent || theme.primary }}>
                Agreement to Terms
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                By accessing or using FleetCam's services, you agree to be bound by these Terms of
                Service and all applicable laws and regulations.
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ mt: 3, color: theme.light || '#fff' }}
              >
                1. Use of Services
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                • You must provide accurate and complete information when creating an account
                <br />
                • You are responsible for maintaining the security of your account credentials
                <br />
                • You may not use our services for any illegal or unauthorized purpose
                <br />• You must not transmit any viruses or malicious code
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: theme.light || '#fff' }}
              >
                2. Subscription and Payment
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                • Subscription fees are billed in advance on a monthly or annual basis
                <br />
                • All fees are non-refundable except as required by law
                <br />
                • We reserve the right to modify pricing with 30 days notice
                <br />• Failure to pay may result in service suspension
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: theme.light || '#fff' }}
              >
                3. Intellectual Property
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                All content, features, and functionality are owned by FleetCam Pty Ltd and are
                protected by international copyright, trademark, and other intellectual property
                laws.
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: theme.light || '#fff' }}
              >
                4. Limitation of Liability
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                FleetCam shall not be liable for any indirect, incidental, special, consequential,
                or punitive damages resulting from your use or inability to use the service.
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: theme.light || '#fff' }}
              >
                5. Termination
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                We may terminate or suspend your account immediately, without prior notice, for
                conduct that we believe violates these Terms or is harmful to other users.
              </Typography>
            </>
          ),
        };

      case 'legal':
        return {
          title: 'Legal Information',
          icon: <Policy />,
          content: (
            <>
              <Typography variant="h6" gutterBottom sx={{ color: theme.accent || theme.primary }}>
                Company Information
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                <strong>FleetCam Pty Ltd</strong>
                <br />
                Registration Number: 2020/123456/07
                <br />
                VAT Number: 4123456789
              </Typography>

              <Box sx={{ my: 3 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                  sx={{ color: theme.light || '#fff' }}
                >
                  Registered Office
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <LocationOn sx={{ color: theme.accent || theme.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="123 Fleet Street"
                      secondary="Sandton, Johannesburg, 2196, South Africa"
                      primaryTypographyProps={{ sx: { color: theme.light || '#fff' } }}
                      secondaryTypographyProps={{
                        sx: { color: alpha(theme.light || '#fff', 0.7) },
                      }}
                    />
                  </ListItem>
                </List>
              </Box>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: theme.light || '#fff' }}
              >
                Compliance & Certifications
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                • ISO 27001:2013 Certified (Information Security)
                <br />
                • POPIA Compliant (Protection of Personal Information Act)
                <br />
                • GDPR Compliant (General Data Protection Regulation)
                <br />• SOC 2 Type II Certified
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: theme.light || '#fff' }}
              >
                Legal Notices
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                This website and all its contents are the property of FleetCam Pty Ltd. Any
                unauthorized use, reproduction, or distribution is strictly prohibited and may
                result in legal action.
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: theme.light || '#fff' }}
              >
                Dispute Resolution
              </Typography>
              <Typography paragraph sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                Any disputes arising from the use of our services shall be governed by the laws of
                South Africa. Parties agree to first attempt resolution through good faith
                negotiations before pursuing legal remedies.
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: theme.light || '#fff' }}
              >
                Contact Legal Department
              </Typography>
              <Typography sx={{ color: alpha(theme.light || '#fff', 0.8) }}>
                Email: legal@fleetcam.com
                <br />
                Phone: +27 (0) 123 456 700
              </Typography>
            </>
          ),
        };

      default:
        return { title: '', icon: null, content: null };
    }
  };

  const { title, icon, content } = getContent();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: alpha(theme.dark || '#1a1a1a', 0.95),
          backgroundImage: 'none',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.accent || theme.primary, 0.2)}`,
          boxShadow: `
            0 0 50px ${alpha(theme.primary, 0.2)},
            0 20px 40px ${alpha('#000', 0.5)}
          `,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: alpha(theme.primary, 0.1),
          borderBottom: `1px solid ${alpha(theme.accent || theme.primary, 0.2)}`,
          color: theme.light || '#fff',
        }}
      >
        <Box sx={{ color: theme.accent || theme.primary }}>{icon}</Box>
        <Typography
          variant="h5"
          component="span"
          fontWeight={600}
          sx={{ color: theme.light || '#fff' }}
        >
          {title}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.light || '#fff',
            '&:hover': {
              backgroundColor: alpha(theme.accent || theme.primary, 0.1),
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2, color: theme.light || '#fff' }}>{content}</DialogContent>

      <Divider sx={{ borderColor: alpha(theme.accent || theme.primary, 0.1) }} />

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: theme.accent || theme.primary,
            color: '#fff',
            fontWeight: 600,
            px: 3,
            '&:hover': {
              backgroundColor: theme.secondary || theme.primary,
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${alpha(theme.accent || theme.primary, 0.3)}`,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LegalModal;
